import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PaletteIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubImageUpload from './ClubImageUpload';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const designSchema = z.object({
    primaryColor: z.string().min(1, 'Cor primária é obrigatória'),
    secondaryColor: z.string().optional(),
    logo: z.string().optional(),
});

function DialogDesign({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(designSchema),
        defaultValues: {
            primaryColor: '#ffffff',
            secondaryColor: '',
            logo: '',
        },
    });

    useEffect(() => {
        if (!open || !data) return;

        form.reset({
            primaryColor: data.primaryColor || '#ffffff',
            secondaryColor: data.secondaryColor || '',
            logo: data.logo || '',
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            if (data?.id) {
                await server.put('club', values);
            }

            await mutate();
            enqueueSnackbar('Dados salvos');
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;
    const logoPreview = form.watch('logo');
    const primaryColor = form.watch('primaryColor');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-md">
                <ScrollDialogHeader>
                    <DialogTitle>Logo e cores</DialogTitle>
                    <DialogDescription>Personalize a identidade visual do seu clube.</DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                    <DialogBody>
                        <FieldGroup>
                            {data?.id ? (
                                <Field>
                                    <FieldLabel>Logo</FieldLabel>
                                    <ClubImageUpload
                                        name="logo"
                                        onChange={(_, value) =>
                                            form.setValue('logo', value ?? '', { shouldDirty: true })
                                        }
                                        value={logoPreview}
                                        canvaWidth={240}
                                        canvaHeight={120}
                                        objectFit="contain"
                                        label="Use PNG com fundo transparente. Recomendado: 240x120px."
                                    />
                                </Field>
                            ) : null}

                            <Field data-invalid={Boolean(errors.primaryColor)}>
                                <FieldLabel htmlFor="primary-color">Cor primária</FieldLabel>
                                <div className="flex items-center gap-3">
                                    <Input
                                        id="primary-color"
                                        type="color"
                                        className="h-12 w-16 shrink-0"
                                        {...form.register('primaryColor')}
                                        aria-invalid={Boolean(errors.primaryColor)}
                                    />
                                    <span className="font-mono text-xs text-muted-foreground">
                                        {primaryColor || '#ffffff'}
                                    </span>
                                </div>
                                <FieldError>{errors.primaryColor?.message}</FieldError>
                            </Field>

                            <Alert>
                                <AlertDescription>
                                    Escolha uma cor e um logo com contraste legível para melhor experiência.
                                </AlertDescription>
                            </Alert>
                        </FieldGroup>
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function DesignClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#personalizar');
    const { health } = useClub();
    const attention = health?.checks?.logo?.ok === false;

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={PaletteIcon}
                        title="Logo e cores"
                        description="Identidade visual do clube."
                        attention={attention}
                    />
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-3 text-sm">
                    <div
                        className="flex h-20 w-[200px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border/60 p-2"
                        style={{ backgroundColor: data?.primaryColor || 'hsl(var(--muted))' }}
                    >
                        {data?.logo ? (
                            <img src={data.logo} alt="Logo do clube" className="max-h-full max-w-full object-contain" />
                        ) : (
                            <span className="text-xs text-muted-foreground">Nenhum logo</span>
                        )}
                    </div>
                    {data?.primaryColor ? (
                        <div className="flex items-center gap-2">
                            <span
                                className="size-4 shrink-0 rounded-full border border-border/60"
                                style={{ backgroundColor: data.primaryColor }}
                            />
                            <span className="text-muted-foreground">Cor primária</span>
                            <span className="font-mono text-xs">{data.primaryColor}</span>
                        </div>
                    ) : null}
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#personalizar" />}>
                        <PaletteIcon className="size-4" />
                        {data?.logo ? 'Alterar' : 'Adicionar'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogDesign data={data} mutate={mutate} open={open} onOpenChange={isOpen => !isOpen && handleClose()} />
        </>
    );
}
